/**
 * Screen 01 · Dashboard. The prototype's `<!-- 01 DASHBOARD -->` markup,
 * against real rows.
 *
 * The prototype switches this screen with a role picker in the corner; here
 * the role is the one the session actually has — `me.org.role` and the venue
 * role for `venueId` — and the views nest rather than replace one another. A
 * manager still gets tonight's prep and tonight's 86 list, because a manager
 * at six o'clock is behind the bar; what they get *as well* is the last
 * count's variance, the week's wastage and the orders waiting on them. An
 * owner or org admin gets those and the money: GP across the menu, the week's
 * spend, and what is left of the AI budget.
 *
 * Every tile loads on its own and fails on its own. Eight tiles read eight
 * features, so one route being down or closed to this role leaves that tile
 * saying so beside seven that still have numbers in them.
 *
 * Nothing here calculates. The roll-ups are `@baros/core`'s `dashboard.ts`,
 * so a figure on this screen is the same arithmetic as the screen it links to.
 */

import type { FeatureId } from "@baros/core";
import { hasFeature } from "../features.js";
import { useEffect, useMemo, useState, type ReactNode } from "react";
import {
  aiBudgetStateFrom,
  analyseMenu,
  gpTargetOf,
  bandTone,
  civilDate,
  costPerMlLookup,
  dashboardRole,
  eightySixed,
  expensesForWeek,
  gpTone,
  menuGpSummary,
  money,
  openTone,
  ordersAwaitingApproval,
  prepSummary,
  ROLE_NAME,
  sees,
  serviceDate,
  varianceRollUp,
  wastageForWeek,
  weekWindow,
  windowLabel,
  type CountHeader,
  type CountLine,
  type MenuGpSummary,
  type Notice,
  type OpenOrders,
  type PeriodExpenses,
  type PeriodWastage,
  type SalesRecord,
  type StockLevel,
  type Tone,
  type VarianceRollUp, clockOf } from "@baros/core";
import { useApp } from "../context.js";
import { expensesApi } from "../expenses/api.js";
import { pct, venueZone } from "../format.js";
import { menuApi } from "../menu/api.js";
import { NoVenue, useActingVenue } from "../NoVenue.js";
import { noticesApi } from "../notices/api.js";
import { prepApi, type PrepDay } from "../prep/api.js";
import type { ScreenProps } from "../screens.js";
import { stockApi } from "../stock/api.js";
import { ordersApi } from "../suppliers/api.js";
import { wastageApi } from "../wastage/api.js";
import { api, ApiError, type Usage } from "../api.js";
import { attempt, LOADING, type Tile } from "./api.js";
import "./dashboard.css";

/** What the variance tile fetches before core rolls it up. */
interface LatestCount {
  readonly count: CountHeader;
  readonly lines: readonly CountLine[];
  readonly stock: readonly StockLevel[];
}

const pad = (n: number) => String(n).padStart(2, "0");

/** How often the clock catches up. A minute's face needs no more than this. */
const TICK_MS = 20_000;

/** How often the notices are read again, on that same tick. */
const NOTICE_REFRESH_MS = 5 * 60_000;

/** `pos` / `warn` / `neg` are theme.css classes; `none` is the body colour. */
function toneClass(tone: Tone): string | undefined {
  return tone === "none" ? undefined : tone;
}

/** Join class names, dropping the empty ones. No `class="v "` in the markup. */
function cx(...names: (string | false | null | undefined)[]): string {
  return names.filter(Boolean).join(" ");
}

export function DashboardScreen(_props: ScreenProps) {
  const app = useApp();
  const { token, me, library, overrides, venueState, go } = app;
  const venueId = useActingVenue();

  const venueRole = venueId
    ? (me.venues.find((v) => v.venueId === venueId)?.role ?? null)
    : null;
  const role = dashboardRole(me.org.role, venueRole);
  // What the organisation has (D14): a tile for a feature that is off is
  // neither fetched nor shown.
  const on = (feature: FeatureId) => hasFeature(me, feature);
  const isManager = sees(role, "manager");
  const isOwner = sees(role, "owner");

  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), TICK_MS);
    return () => clearInterval(id);
  }, []);

  // Everything on this screen that says "tonight" or "this week" is decided
  // on the venue's clock, not the tablet's and not UTC. A tablet carried
  // interstate, or a Lambda in ap-southeast-2 answering at 13:00Z on a
  // Sunday, must still call it Sunday's service and this week's figures —
  // and the label and the numbers under it must come from the same call.
  // The venue's clock: its zone, the hour its day ends and the day its week
  // starts (3 am and Monday unless it set its own). `tz` is the zone alone,
  // for showing times.
  const venueClock = clockOf(venueState?.venue);
  const tz = venueClock.timeZone;
  const date = serviceDate(now, { timeZone: venueClock });
  const week = useMemo(() => weekWindow(date, venueClock.weekStartsOn), [date, venueClock.weekStartsOn]);

  // ------------------------------------------------------------ what loads

  const [prep, setPrep] = useState<Tile<PrepDay>>(LOADING);
  const [latest, setLatest] = useState<Tile<LatestCount | null>>(LOADING);
  const [waste, setWaste] = useState<Tile<PeriodWastage>>(LOADING);
  const [orders, setOrders] = useState<Tile<OpenOrders>>(LOADING);
  const [sales, setSales] = useState<Tile<readonly SalesRecord[]>>(LOADING);
  const [spend, setSpend] = useState<Tile<PeriodExpenses>>(LOADING);

  useEffect(() => {
    if (!token || !venueId) return;
    let live = true;
    if (on("prep")) {
      void attempt(() => prepApi.day(token, venueId, date), "No prep list yet").then(
        (t) => live && setPrep(t),
      );
    }
    return () => {
      live = false;
    };
  }, [token, venueId, date]);

  // What the nightly and hourly jobs found (#81). Read for every role: a
  // bartender on at five can raise the order a cut-off reminder is about.
  // Read again every few ticks rather than once: the hourly job clears a
  // cut-off reminder when the order goes in, and a dashboard left open on the
  // bar all evening should notice. A failed re-read keeps the cards it had.
  const noticeRound = Math.floor(now.getTime() / NOTICE_REFRESH_MS);
  const [notices, setNotices] = useState<Tile<readonly Notice[]>>(LOADING);
  const [dismissError, setDismissError] = useState<string | null>(null);
  useEffect(() => {
    if (!token || !venueId || !on("notices")) return;
    let live = true;
    void attempt(async () => (await noticesApi.list(token, venueId)).notices).then(
      (t) => live && setNotices((prev) => (t.state === "ok" || prev.state !== "ok" ? t : prev)),
    );
    return () => {
      live = false;
    };
  }, [token, venueId, noticeRound]);

  // Cut-off reminders are anyone's who can order; stock and count notices
  // are a manager's to act on, as the variance tile below is. A notice past
  // its `expiresAt` goes on the screen's own clock, so a cut-off that has
  // passed stops saying "Closing now" without waiting for the next read.
  const attention = useMemo(
    () =>
      notices.state === "ok"
        ? notices.value.filter(
            (n) =>
              (isManager || n.kind === "cutoff") &&
              on(n.kind === "cutoff" ? "orderReminders" : "stockNotices") &&
              Date.parse(n.expiresAt) > now.getTime(),
          )
        : [],
    [notices, isManager, now, me],
  );

  /**
   * Clear a card. Taken off the screen straight away and put back if the API
   * refuses, so a slow connection does not leave a manager pressing twice. A
   * 404 means the job got there first; the card was going anyway.
   */
  const dismiss = (n: Notice) => {
    if (!token || !venueId || notices.state !== "ok") return;
    const before = notices;
    setDismissError(null);
    setNotices({ state: "ok", value: notices.value.filter((x) => x.noticeId !== n.noticeId) });
    noticesApi.dismiss(token, venueId, n.noticeId).catch((err: unknown) => {
      if (err instanceof ApiError && err.status === 404) return;
      setNotices(before);
      setDismissError(err instanceof Error ? err.message : "Could not dismiss that notice");
    });
  };

  useEffect(() => {
    if (!token || !venueId || !isManager) return;
    let live = true;

    if (on("stocktake")) void attempt(async (): Promise<LatestCount | null> => {
      const { counts } = await stockApi.counts(token, venueId);
      // The last finished count is the one a variance means something for; an
      // open one is half a count and would read as a hundred missing bottles.
      const header = counts.find((c) => c.status === "finalised") ?? counts[0];
      if (!header) return null;
      const [detail, levels] = await Promise.all([
        stockApi.count(token, venueId, header.countId),
        stockApi.levels(token, venueId),
      ]);
      return { count: detail.count, lines: detail.lines, stock: levels.stock };
    }, "No count on file yet").then((t) => live && setLatest(t));

    if (on("wastage")) void attempt(async () => {
      const { wastage } = await wastageApi.list(token, venueId);
      return wastageForWeek(wastage, week, venueClock);
    }).then((t) => live && setWaste(t));

    // Screen 10's own list. A 403 or a 404 leaves this one tile saying so
    // and the rest of the screen with numbers in it.
    if (on("orders")) void attempt(async () => {
      const { orders } = await ordersApi.orders(token, venueId);
      return ordersAwaitingApproval(orders);
    }, "Orders are not set up here yet").then((t) => live && setOrders(t));

    return () => {
      live = false;
    };
  }, [token, venueId, isManager, week, tz]);

  useEffect(() => {
    if (!token || !venueId || !isOwner) return;
    let live = true;

    if (on("menu")) void attempt(async () => (await menuApi.sales(token, venueId)).sales).then(
      (t) => live && setSales(t),
    );
    if (on("expenses")) void attempt(async () => {
      const { expenses } = await expensesApi.list(token, venueId);
      return expensesForWeek(expenses, week, venueClock);
    }).then((t) => live && setSpend(t));

    return () => {
      live = false;
    };
  }, [token, venueId, isOwner, week, tz]);

  // ------------------------------------------------------- what it adds up to

  const ingredientsById = useMemo(
    () => new Map(library.ingredients.map((i) => [i.ingredientId, i])),
    [library.ingredients],
  );

  const offTonight = useMemo(
    () => eightySixed(library.specs, overrides),
    [library.specs, overrides],
  );

  const tonight = useMemo(
    () => (prep.state === "ok" ? prepSummary(prep.value.tabs) : null),
    [prep],
  );

  const variance: VarianceRollUp | null = useMemo(() => {
    if (latest.state !== "ok" || latest.value === null) return null;
    return varianceRollUp(
      latest.value.lines,
      costPerMlLookup(ingredientsById, latest.value.stock),
    );
  }, [latest, ingredientsById]);

  const gp: MenuGpSummary | null = useMemo(() => {
    if (sales.state !== "ok") return null;
    return menuGpSummary(
      analyseMenu({
        specs: library.specs,
        ingredientsById,
        overridesBySpecId: overrides,
        salesBySpecId: new Map(sales.value.map((s) => [s.specId, s])),
        gpTarget: gpTargetOf(venueState?.venue),
      }),
    );
  }, [sales, library.specs, ingredientsById, overrides, venueState]);

  // `aiBudgetStateFrom` rather than the cap alone: a new organisation has no
  // cap, and a tile that vanishes when there is none reads as a bug. It also
  // keeps "nobody has set one" apart from "it is set to zero", which is a
  // different and worse state — zero refuses every chat turn.
  const aiState = useMemo(
    () => aiBudgetStateFrom(me.org.settings),
    [me.org.settings],
  );

  // What the chat has spent this month, from the counters it writes (D10).
  // Six periods so the tile can show a short history strip beneath the
  // remaining figure, not just this month's total.
  const [aiSpend, setAiSpend] = useState<Tile<Usage>>(LOADING);
  useEffect(() => {
    if (!token) return;
    void attempt(() => api.usage(token, 6), "Spend is not published yet").then(setAiSpend);
  }, [token]);

  const ingredientName = (id: string) => ingredientsById.get(id)?.name ?? id;

  // The bar's clock, not the tablet's: Fig. 1.1 sits directly above the
  // service date and the trading week, and the three have to agree.
  const wall = civilDate(now, tz);
  const clock = `${pad(wall.hour)}:${pad(wall.minute)}`;
  const dayLine = now.toLocaleDateString("en-AU", {
    timeZone: tz,
    weekday: "long",
    day: "numeric",
    month: "long",
  });

  // A brand-new organisation has no venues, so this is the first screen its
  // owner lands on. What it must not do is tell them to ask an admin: they
  // are the admin, and the real problem is the empty org. `NoVenue` tells
  // the two apart.
  if (!venueId) return <NoVenue what="tonight's figures" />;

  return (
    <div className="screen">
      <div className="split dash-split">
        {/* ---------------------------------------------- now */}
        <section className="dash-now">
          <div>
            <div className="label">now</div>
            <div className="dash-clock">{clock}</div>
            <div className="dash-day">{dayLine}</div>
          </div>
          <div className="rule" />
          <div className="dash-stats">
            <div className="dash-stat">
              <span className="label">Venue</span>
              <span className="v" style={{ fontFamily: "var(--sans)", fontSize: 14 }}>
                {venueState?.venue.name ?? venueId}
              </span>
            </div>
            <div className="dash-stat">
              <span className="label">Service date</span>
              <span className="v">{date}</span>
            </div>
            <div className="dash-stat">
              <span className="label">Trading week</span>
              <span className="v">{windowLabel(week)}</span>
            </div>
            <div className="dash-stat">
              <span className="label">Prep open</span>
              <span className={cx("v", tonight && toneClass(openTone(tonight.open)))}>
                {tonight ? `${tonight.open} / ${tonight.total}` : "—"}
              </span>
            </div>
            <div className="dash-stat">
              <span className="label">86'd</span>
              <span className={cx("v", offTonight.length > 0 && "neg")}>
                {offTonight.length}
              </span>
            </div>
          </div>
          <div className="rule" />
          <div className="dash-foot-note">
            Surfacing by time
            <br />
            of day · {ROLE_NAME[role]} view
          </div>
        </section>

        {/* ------------------------------------------------------- tiles */}
        <section className="dash-main">
          {/* Needs attention — what the scheduled checks found. Absent when
              there is nothing, because a card saying "all clear" every day
              teaches people to stop reading it. */}
          {(attention.length > 0 || dismissError || notices.state === "error") && (
            <div className="dash-block">
              <div className="label">
                Needs attention{attention.length > 0 ? ` · ${attention.length}` : ""}
              </div>
              <div className="dash-list">
                {notices.state === "error" && (
                  <div className="dash-note err">{notices.message}</div>
                )}
                {dismissError && <div className="dash-note err">{dismissError}</div>}
                {attention.map((n) => (
                  <div className={cx("dash-attn-item", noticeTone(n))} key={n.noticeId}>
                    <div className="dash-attn-top">
                      <button className="dash-attn-title" onClick={() => go({ kind: n.screen })}>
                        {n.title}
                      </button>
                      {n.kind === "cutoff" && (
                        <span className="dash-attn-when">{closesIn(n.expiresAt, now)}</span>
                      )}
                      <button
                        className="dash-more"
                        onClick={() => dismiss(n)}
                        aria-label={`Dismiss: ${n.title}`}
                      >
                        Dismiss
                      </button>
                    </div>
                    <div className="dash-note">{n.detail}</div>
                    {n.rows.length > 0 && (
                      <div className="dash-attn-rows">
                        {n.rows.map((row, i) => (
                          <div className="dash-attn-row" key={`${i}-${row.label}`}>
                            <span>{row.label}</span>
                            <span className={cx("v", toneClass(row.tone))}>{row.value}</span>
                          </div>
                        ))}
                        {n.more > 0 && (
                          <div className="dash-attn-row">
                            <span style={{ color: "var(--t2)" }}>and {n.more} more</span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* tonight's prep, by tab */}
          {on("prep") && (
          <div className="dash-block">
            <div className="dash-head">
              <div className="label">
                tonight's prep
                {tonight ? ` · ${tonight.open} open` : ""}
              </div>
              <button className="dash-more" onClick={() => go({ kind: "prep" })}>
                All prep →
              </button>
            </div>
            <TileBody tile={prep}>
              {() =>
                tonight && tonight.total > 0 ? (
                  <div className="dash-list">
                    {tonight.tabs.map((tab) => (
                      <button
                        key={tab.tab}
                        className="dash-list-row"
                        onClick={() => go({ kind: "prep", params: { tab: tab.tab } })}
                      >
                        <span className="name">{tab.tab}</span>
                        <span className={cx("count", toneClass(openTone(tab.open)))}>
                          {tab.open} / {tab.total}
                        </span>
                      </button>
                    ))}
                  </div>
                ) : (
                  <div className="dash-list">
                    <div className="dash-note">
                      Nothing is on tonight's prep list. A manager writes it on screen 06.
                    </div>
                  </div>
                )
              }
            </TileBody>
          </div>
          )}

          {/* 86'd, and the way to the count */}
          <div className="dash-row">
            <div className="dash-block">
              <div className="label">86'd tonight</div>
              <div className="dash-list">
                {offTonight.length === 0 ? (
                  <div className="dash-note">
                    The full list is on. Nothing has been taken off at this venue.
                  </div>
                ) : (
                  offTonight.map((drink) => (
                    <button
                      key={drink.specId}
                      className="dash-list-row"
                      onClick={() =>
                        go({ kind: "recipes", params: { specId: drink.specId } })
                      }
                    >
                      <span className="dot" style={{ background: "var(--neg)" }} />
                      <span className="name serif">{drink.name}</span>
                      <span className="why">{drink.reason}</span>
                    </button>
                  ))
                )}
              </div>
            </div>

            {on("stocktake") && (
            <div className="dash-block">
              <div className="label">stock</div>
              <button className="dash-card" onClick={() => go({ kind: "count" })}>
                <span className="label">Last count</span>
                <span className="big sm">
                  {latest.state === "ok" && latest.value
                    ? shortWhen(
                        latest.value.count.finishedAt ?? latest.value.count.startedAt,
                        tz,
                      )
                    : latest.state === "loading"
                      ? "…"
                      : "None"}
                </span>
                <span className="sub">
                  {latest.state === "ok" && latest.value
                    ? `${latest.value.count.lineCount} lines · ${latest.value.count.method === "weigh" ? "by weight" : "by tenths"} · ${latest.value.count.status}`
                    : "No count has been finalised at this venue yet."}
                </span>
                <span className="go">Open the stocktake →</span>
              </button>
            </div>
            )}
          </div>

          {/* -------------------------------------------------- manager */}
          {isManager && (
            <>
              {on("stocktake") && (
              <div className="dash-block">
                <div className="dash-head">
                  <div className="label">
                    variance, last count
                    {variance ? ` · ${variance.counts.watch} watch · ${variance.counts.investigate} investigate` : ""}
                  </div>
                  <button className="dash-more" onClick={() => go({ kind: "count" })}>
                    Open the count →
                  </button>
                </div>
                <TileBody tile={latest}>
                  {(value) =>
                    value === null || variance === null ? (
                      <div className="dash-list">
                        <div className="dash-note">
                          No count on file yet. Screen 05 opens one.
                        </div>
                      </div>
                    ) : variance.flagged.length === 0 ? (
                      <div className="dash-list">
                        <div className="dash-note">
                          All {variance.lines} lines are inside pouring tolerance.
                        </div>
                      </div>
                    ) : (
                      <div className="dash-list dash-var">
                        <div className="thead">
                          <span className="c-item">Item</span>
                          <span className="c-exp">Expected mL</span>
                          <span className="c-got">Counted mL</span>
                          <span className="c-diff">Variance</span>
                          <span className="c-cost">At cost</span>
                        </div>
                        {variance.flagged.map((row) => {
                          const tone = toneClass(bandTone(row.band));
                          return (
                            <div className="dash-var-row" key={row.ingredientId}>
                              <span className="c-item">{ingredientName(row.ingredientId)}</span>
                              <span className="c-exp">{Math.round(row.expectedMl)}</span>
                              <span className="c-got">{Math.round(row.countedMl)}</span>
                              <span className={cx("c-diff", tone)}>
                                {signed(Math.round(row.deltaMl))} · {pct(row.percent)}
                              </span>
                              <span className={cx("c-cost", tone)}>
                                {money(row.costDollars)}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    )
                  }
                </TileBody>
              </div>
              )}

              <div className="dash-row">
                {on("wastage") && (
                <div className="dash-block">
                  <div className="label">Wastage · week of {windowLabel(week)}</div>
                  <TileBody tile={waste}>
                    {(value) => (
                      <button className="dash-card" onClick={() => go({ kind: "waste" })}>
                        <span className="label">Logged this week</span>
                        <span className={cx("big", value.costDollars > 0 ? "warn" : "pos")}>
                          {money(value.costDollars)}
                        </span>
                        <span className="sub">
                          {value.count === 0
                            ? "Nothing logged this week."
                            : value.reasons
                                .slice(0, 3)
                                .map((r) => `${r.count} ${r.reason.toLowerCase()}`)
                                .join(" · ")}
                        </span>
                        <span className="go">Log wastage →</span>
                      </button>
                    )}
                  </TileBody>
                </div>
                )}

                {on("stocktake") && (
                <div className="dash-block">
                  <div className="label">Stock at cost · last count</div>
                  <button className="dash-card" onClick={() => go({ kind: "count" })}>
                    <span className="label">Shortfall</span>
                    <span className={cx("big", variance && variance.shortfallDollars > 0 ? "neg" : "pos")}>
                      {variance ? money(variance.shortfallDollars) : "—"}
                    </span>
                    <span className="sub">
                      {variance
                        ? `${variance.counts.ok} within tolerance of ${variance.lines} · net ${money(variance.netCostDollars)}`
                        : "Waiting on a finalised count."}
                    </span>
                    <span className="go">Review the count →</span>
                  </button>
                </div>
                )}

                {on("orders") && (
                <div className="dash-block">
                  <div className="label">Orders · awaiting approval</div>
                  <TileBody tile={orders}>
                    {(value) => (
                      <button className="dash-card" onClick={() => go({ kind: "order" })}>
                        <span className="label">Held for approval</span>
                        <span className={cx("big", value.awaiting.length > 0 ? "warn" : "pos")}>
                          {value.awaiting.length}
                        </span>
                        <span className="sub">
                          {value.awaiting.length === 0
                            ? `Nothing waiting · ${value.total} on file`
                            : `${money(value.valueIncGstCents / 100)} over the raiser's limit`}
                          {value.drafts > 0
                            ? ` · ${value.drafts} draft${value.drafts === 1 ? "" : "s"}`
                            : ""}
                        </span>
                        <span className="go">Open orders →</span>
                      </button>
                    )}
                  </TileBody>
                </div>
                )}
              </div>
            </>
          )}

          {/* ---------------------------------------------------- owner */}
          {isOwner && (
            <div className="dash-row">
              {on("menu") && (
              <div className="dash-block">
                <div className="dash-head">
                  <div className="label">gross profit across the menu</div>
                  <span className="label">
                    Target {gp ? pct(gp.target) : pct(74)}
                  </span>
                </div>
                <TileBody tile={sales}>
                  {() =>
                    gp === null || gp.itemCount === 0 ? (
                      <div className="dash-card">
                        <span className="dash-note">
                          Nothing on the menu carries a venue price yet, so there is no
                          GP to take. Screen 07 sets them.
                        </span>
                      </div>
                    ) : (
                      <button className="dash-card" onClick={() => go({ kind: "menu" })}>
                        <span className="label">Weighted by what sells</span>
                        <span className={cx("big", toneClass(gpTone(gp.weightedGp, gp.target)))}>
                          {pct(gp.weightedGp)}
                        </span>
                        <span className="dash-meter">
                          <span
                            className={toneClass(gpTone(gp.weightedGp, gp.target))}
                            style={{ width: `${Math.max(0, Math.min(100, gp.weightedGp))}%` }}
                          />
                        </span>
                        <span className="sub">
                          {gp.itemCount} drinks · mean {pct(gp.meanGp)} · {gp.belowTarget} under
                          target
                          {gp.unpricedCount > 0 ? ` · ${gp.unpricedCount} unpriced` : ""}
                          <br />
                          {money(gp.totalWeeklyMargin)} contribution on{" "}
                          {money(gp.totalWeeklyRevenue)} a week
                        </span>
                        <span className="go">Menu engineering →</span>
                      </button>
                    )
                  }
                </TileBody>
              </div>
              )}

              {on("expenses") && (
              <div className="dash-block">
                <div className="label">Expenses · week of {windowLabel(week)}</div>
                <TileBody tile={spend}>
                  {(value) => (
                    <button className="dash-card" onClick={() => go({ kind: "exp" })}>
                      <span className="label">Spent this week</span>
                      <span className="big">{money(value.amountIncGst)}</span>
                      <span className="sub">
                        {value.count} slip{value.count === 1 ? "" : "s"} · {money(value.gst)} GST
                        {value.unmatched > 0 ? ` · ${value.unmatched} unmatched` : ""}
                      </span>
                      <span className="go">Open expenses →</span>
                    </button>
                  )}
                </TileBody>
              </div>
              )}

              {on("chat") && (
              <div className="dash-block">
                <div className="label">AI budget · this month</div>
                {aiState.kind === "none" ? (
                  // Not a figure, but still a tile: the owner of a new org
                  // needs to read why there is no number here, and a cap
                  // clamped to zero is a fault rather than a blank.
                  <button className="dash-card" onClick={() => go({ kind: "org" })}>
                    <span className="label">Monthly cap</span>
                    <span
                      className="big"
                      style={aiState.absence === "zero" ? { color: "var(--warn)" } : undefined}
                    >
                      {aiState.absence === "zero" ? money(0) : "—"}
                    </span>
                    <span className="sub">
                      {aiState.title}
                      <br />
                      {aiState.detail}
                    </span>
                    <span className="go">Organisation settings →</span>
                  </button>
                ) : (
                  <button className="dash-card" onClick={() => go({ kind: "org" })}>
                    <span className="label">
                      {aiSpend.state === "ok" ? "Remaining this month" : "Monthly cap"}
                    </span>
                    <span
                      className="big"
                      style={{
                        color:
                          aiSpend.state === "ok" && aiSpend.value.org.costCents >= aiState.budget.effectiveCents * 0.8
                            ? "var(--warn)"
                            : undefined,
                      }}
                    >
                      {aiSpend.state === "ok"
                        ? money(Math.max(0, aiState.budget.effectiveCents - aiSpend.value.org.costCents) / 100)
                        : money(aiState.budget.effectiveCents / 100)}
                    </span>
                    <span className="sub">
                      {aiSpend.state === "ok"
                        ? `${money(aiSpend.value.org.costCents / 100)} spent of ${money(aiState.budget.effectiveCents / 100)} · ${aiSpend.value.org.turns} turn${aiSpend.value.org.turns === 1 ? "" : "s"}${aiSpend.value.org.storageCents > 0 ? ` · ${money(aiSpend.value.org.storageCents / 100)} of it the recipe shelf` : ""}`
                        : aiSpend.state === "loading"
                          ? "Reading this month’s spend…"
                          : aiSpend.state === "unavailable"
                            ? aiSpend.reason
                            : aiSpend.message}
                      <br />
                      {aiState.budget.ceilingCents === null
                        ? "No plan ceiling on file."
                        : `Plan allows ${money(aiState.budget.ceilingCents / 100)}`}
                      {aiState.budget.limitCents > (aiState.budget.ceilingCents ?? aiState.budget.limitCents) ? " · clamped to the plan" : ""}
                    </span>
                    {aiSpend.state === "ok" && aiSpend.value.history.length > 1 && (
                      <div className="dash-ai-history">
                        {[...aiSpend.value.history].reverse().map((h) => {
                          const heightPct =
                            aiState.budget.effectiveCents > 0
                              ? Math.max(2, Math.min(100, (h.org.costCents / aiState.budget.effectiveCents) * 100))
                              : 2;
                          return (
                            <div className="dash-ai-month" key={h.period}>
                              <span className="dash-ai-track">
                                <span
                                  className={cx(
                                    "bar",
                                    toneClass(h.org.costCents >= aiState.budget.effectiveCents * 0.8 ? "warn" : "none"),
                                  )}
                                  style={{ height: `${heightPct}%` }}
                                />
                              </span>
                              <span className="amt">{money(h.org.costCents / 100)}</span>
                              <span className="lbl">{monthLabel(h.period)}</span>
                            </div>
                          );
                        })}
                      </div>
                    )}
                    <span className="go">Organisation settings →</span>
                  </button>
                )}
              </div>
              )}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

/**
 * A tile's four states in one place.
 *
 * `unavailable` is not painted as a failure: an unbuilt route and an empty
 * venue both read as a sentence in the muted colour, which is what they are.
 */
function TileBody<T>({
  tile,
  children,
}: {
  readonly tile: Tile<T>;
  readonly children: (value: T) => ReactNode;
}) {
  if (tile.state === "loading") {
    return (
      <div className="dash-card">
        <span className="dash-note">Loading…</span>
      </div>
    );
  }
  if (tile.state === "unavailable") {
    return (
      <div className="dash-card">
        <span className="dash-note">{tile.reason}</span>
      </div>
    );
  }
  if (tile.state === "error") {
    return (
      <div className="dash-card">
        <span className="dash-note err">{tile.message}</span>
      </div>
    );
  }
  return <>{children(tile.value)}</>;
}

/**
 * The colour down a notice's left edge: red when any line on it is red, amber
 * for a cut-off or anything amber, plain otherwise.
 */
function noticeTone(n: Notice): string | undefined {
  if (n.rows.some((r) => r.tone === "neg")) return "neg";
  if (n.kind === "cutoff" || n.rows.some((r) => r.tone === "warn")) return "warn";
  return undefined;
}

/**
 * `Closes in 3 h 20 min`, counted down on the dashboard's own tick. The
 * notice says when the cut-off is; how long is left changes every minute and
 * is the screen's to work out, so the job can write the same row each hour.
 */
function closesIn(expiresAt: string, now: Date): string {
  const minutes = Math.floor((Date.parse(expiresAt) - now.getTime()) / 60_000);
  if (!(minutes > 0)) return "Closing now";
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h === 0) return `Closes in ${m} min`;
  return m === 0 ? `Closes in ${h} h` : `Closes in ${h} h ${m} min`;
}

/** `-140` → `−140`, `70` → `+70`. The prototype's minus sign, not a hyphen. */
function signed(n: number): string {
  if (n === 0) return "0";
  return n > 0 ? `+${n}` : `−${Math.abs(n)}`;
}

/** `2026-09-19T…` → `Fri 19 Sep`, on the venue's calendar rather than the tablet's. */
function shortWhen(iso: string, timeZone: string): string {
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return "—";
  return at.toLocaleDateString("en-AU", {
    timeZone,
    weekday: "short",
    day: "2-digit",
    month: "short",
  });
}

/** `2026-09` → `Sep`, for the AI spend history strip's month labels. */
function monthLabel(period: string): string {
  const [year, month] = period.split("-").map(Number);
  const at = new Date(Date.UTC(year ?? 1970, (month ?? 1) - 1, 1));
  return at.toLocaleDateString("en-AU", { timeZone: venueZone(), month: "short" });
}
